// --------------------------------------------------------------
// Created On: 2025-04-03
// Author: Dimitra Weinstein
//
// Last Modified: 2025-11-06
// Modified By: Zachary Thomas
//
// Copyright 2025 © Cornell Pump Company, All Rights Reserved
// --------------------------------------------------------------

import React, { Fragment, useMemo } from "react";
import { MAX_IMPELLER_ANGLE, MAX_IMPELLER_TRIM, MAX_ITEM_QUANTITY } from "../../../constants/miscellaneous";
import ColorPicker from "../../../components/ColorPicker/ColorPicker";
import LeadTime from "../../../components/LeadTime/LeadTime";
import styles from "../QuickOrderPage.module.scss";

// This component is used to display a single item in the quick order list.
export default function QuickOrderListItemForm(props: Props): Component {
  // Get the selected paint color.
  const selectedPaintColor = useMemo(
    () => getPaintColor(props.paintColors, props.selectedPaintColorId),
    [JSON.stringify(props.paintColors), props.selectedPaintColorId]
  );

  // Get the selected paint color.
  function getPaintColor(paintColors: PaintColor[], selectedPaintColorId: number | null): PaintColor | null {
    const paintColorIndex = paintColors.findIndex((paintColor) => paintColor.paintColorId === selectedPaintColorId);
    if (paintColorIndex > -1) {
      return paintColors[paintColorIndex];
    } else {
      return null;
    }
  }

  // Get discounted price of part.
  function getDiscountedPrice(price: number | null, companyDiscount: number | null): string {
    if (price === null) {
      return "N/A";
    } else if (companyDiscount === null || companyDiscount === undefined) {
      return price.toLocaleString("en-US", {
        style: "currency",
        currency: "USD",
        minimumFractionDigits: 2,
        maximumFractionDigits: 2,
      });
    } else {
      return (price - price * (companyDiscount / 100)).toLocaleString("en-US", {
        style: "currency",
        currency: "USD",
        minimumFractionDigits: 2,
        maximumFractionDigits: 2,
      });
    }
  }

  return (
    <Fragment>
      <form
        data-test="quick-order-form"
        className={`row m-2 g-3 p-3 justify-content-center align-items-center ${styles.quickOrderPage} border-top`}
      >
        <div className="col">
          <div className={!props.isImpeller ? "row mt-3 align-items-center" : "row align-items-center"}>
            {/* Line Item Number */}
            <div className="col col-lg-1 text-center">
              <p data-test="quick-order-list-item-form-line-item-number" className="medium card-text mt-2 fs-3">
                {props.lineItemNumber}.
              </p>
            </div>

            {/* Part Number */}
            <div className={`col col-lg-2 mb-3 text-center`}>
              <label className="small mb-1 fw-medium fs-6" htmlFor="partNumber">
                Part Number
              </label>
              <input
                data-test="quick-order-list-item-form-part-number-input"
                className={`form-control ${!props.partNumberInvalid ? "" : styles.inputError}`}
                type="search"
                id="partNumber"
                name="partNumber"
                placeholder="Enter part number"
                value={props.partNumber}
                onChange={(e) => {
                  e.preventDefault(), props.onPartNumberChange(e.target.value), props.onErrorMessageChange("");
                }}
                onBlur={() => {
                  props.onPartNumberSearch(props.partNumber);
                }}
                onKeyDown={(e) => {
                  if (e.key === "Enter") {
                    e.preventDefault(), props.onErrorMessageChange(""), props.onPartNumberSearch(props.partNumber);
                  }
                }}
              />
              <div className="text-danger small mt-1">
                {props.partNumberInvalid && props.errorMessage.length > 0 && (
                  <span data-test="quick-order-list-item-form-part-number-error-message">{props.errorMessage}</span>
                )}
              </div>
            </div>

            {/* Description */}
            <div className={`col col-lg-2 mb-3 text-center`}>
              <label className="small mb-2 fw-medium fs-6" htmlFor="description">
                Description
              </label>
              <p data-test="quick-order-form-part-description" className="small card-text mx-2 fw-medium fs-6">
                {props.description}
              </p>
            </div>

            {/* Quantity Input */}
            <div className={`col text-center mb-3`}>
              <label className="small mx-auto mb-1 fw-medium fs-6" htmlFor="partNumber">
                Quantity ({props.unitName})
              </label>
              <input
                data-test="quick-order-form-quantity-input"
                className={`form-control form-control-sm mb-1 text-center ${
                  Number(props.quantity) <= 0 ? styles.inputError : ""
                }`}
                type="text"
                id="quantity"
                name="quantity"
                maxLength={10}
                max={MAX_ITEM_QUANTITY}
                value={props.quantity}
                placeholder="Enter quantity"
                onChange={(e) => {
                  props.onQuantityChange(e.target.value), props.onErrorMessageChange("");
                }}
                autoFocus={props.searchComplete}
                required
              />
              <div className="text-danger small">
                {Number(props.quantity) <= 0 && (
                  <span data-test="quick-order-list-item-form-quantity-error-message">{"Enter a valid quantity."}</span>
                )}
              </div>
            </div>

            {/* List Price */}
            <div className="col mb-3 text-center">
              <label className="small mx-1 mb-1 fw-medium fs-6" htmlFor="price">
                Price
              </label>
              <div>
                {props.price !== null ? (
                  <p data-test="quick-order-form-item-price" className="small card-text fs-6 mx-1 p-2">
                    {props.price.toLocaleString("en-US", {
                      style: "currency",
                      currency: "USD",
                      minimumFractionDigits: 2,
                      maximumFractionDigits: 2,
                    })}
                  </p>
                ) : (
                  <p data-test="quick-order-form-item-price" className="small card-text fs-6 mx-1 p-2">
                    N/A
                  </p>
                )}
              </div>
            </div>

            {/* Discount Price */}
            <div className="col mb-3 text-center">
              <label className="small mx-1 fw-medium fs-6" htmlFor="discountPrice">
                Discount Price
              </label>
              <div>
                {props.price !== null ? (
                  <p data-test="quick-order-form-item-discount-price" className="small card-text fs-6 mx-1 p-2">
                    {getDiscountedPrice(props.price, props.companyDiscount)}
                  </p>
                ) : (
                  <p data-test="quick-order-form-item-discount-price" className="small card-text fs-6 mx-1 p-2">
                    N/A
                  </p>
                )}
              </div>
            </div>

            {/* Weight */}
            <div className="col mb-3 text-center">
              <label className="small justify-content-center px-1 mx-2 mb-1 fw-medium fs-6" htmlFor="weight">
                Weight
              </label>
              <div>
                <p data-test="quick-order-form-item-weight" className="small card-text fs-6 mx-1 p-2">
                  {props.weight === null || props.weight === 0 ? <span>N/A</span> : `${props.weight} lbs`}
                </p>
              </div>
            </div>

            {/* Lead Time */}
            {props.isSellable && (
              <div className="col mb-3 mx-auto text-center">
                <label className="small justify-content-center mb-1 fw-medium fs-6" htmlFor="leadTime">
                  Ship Time
                </label>
                <div
                  data-test="quick-order-form-lead-time"
                  className={`small card-text fs-6 ${styles.leadTimeWrapper}`}
                >
                  <LeadTime
                    className="small pt-2"
                    partNumber={props.partNumber}
                    quantity={props.quantity}
                    paintColorId={props.isPaintable ? props.selectedPaintColorId : null}
                    isImpeller={props.isImpeller}
                    impellerAngle={props.angle}
                    impellerTrim={props.trim}
                    onLeadTimeCalculated={() => {
                      /* Do nothing. */
                    }}
                  />
                </div>
              </div>
            )}

            {/* Paint Color */}
            {props.isPaintable && (
              <div className="col mb-3 text-center">
                <label className="small pb-1 fw-medium fs-6" htmlFor="paintColor">
                  Paint Color
                </label>
                <div
                  data-test="quick-order-form-color-picker"
                  className={`small fs-6 text-center ${styles.colorPickerWrapper}`}
                >
                  <ColorPicker
                    colors={props.paintColors}
                    selectedColorId={selectedPaintColor ? selectedPaintColor.paintColorId : null}
                    onChange={(paintColorId) => props.onPaintColorChange(paintColorId)}
                  />
                </div>
                <div className="text-danger small">
                  {props.selectedPaintColorId === null && (
                    <span data-test="quick-order-list-item-form-quantity-error-message">{"Select a paint color."}</span>
                  )}
                </div>
              </div>
            )}
          </div>

          {/* Impeller Trim & Angle, only displayed when an impeller part number is entered into the form. */}
          <div className="row mx-3 my-3 justify-content-around align-items-center">
            {/* Impeller Angle */}
            {props.isImpeller && (
              <div className="col col-lg-4 mb-1 px-4">
                <label className="small mx-auto mb-2 fw-medium fs-6" htmlFor="angle">
                  Impeller Angle (degrees)
                </label>
                <input
                  data-test="quick-order-form-angle-input"
                  className={`form-control form-control-sm text-center fs-6 mb-1 ${
                    props.angle === null || props.angle === "" ? styles.inputError : ""
                  }`}
                  type="text"
                  id="angle"
                  name="angle"
                  maxLength={10}
                  max={MAX_IMPELLER_ANGLE}
                  value={props.angle !== null ? props.angle : ""}
                  onChange={(e) => {
                    props.onAngleChange(e.target.value), props.onErrorMessageChange("");
                  }}
                  onWheel={(e) => e.currentTarget.blur()}
                  placeholder="Enter angle"
                />
                <div className="text-danger small text-center">
                  {(props.angle === null || props.angle === "") && (
                    <span data-test="quick-order-list-item-form-impeller-angle-error-message">
                      {"Enter a valid angle."}
                    </span>
                  )}
                </div>
              </div>
            )}

            {/* Impeller Trim */}
            {props.isImpeller && (
              <div className="col col-lg-4 mb-1 px-4">
                <label className="small mx-auto mb-1 fw-medium fs-6" htmlFor="trim">
                  Impeller Trim (inches)
                </label>
                <select
                  className={`form-select form-control mb-1 text-center ${
                    props.trimOption === null || props.trimOption === "" ? styles.inputError : ""
                  }`}
                  value={props.trimOption === null ? "" : props.trimOption}
                  onChange={(e) => {
                    props.onTrimOptionChange(e.target.value);
                  }}
                >
                  <option value="" disabled>
                    Select Trim
                  </option>
                  <option value="full-trim">Full Trim</option>
                  <option value="custom">Custom</option>
                </select>
                {props.trimOption === "custom" && props.trim !== null && (
                  <input
                    className={`text-center form-control text-center ${props.trim === null ? styles.inputError : ""}`}
                    data-test="impeller-trim-input"
                    type="text"
                    maxLength={10}
                    max={MAX_IMPELLER_TRIM}
                    value={props.trim}
                    onChange={(e) => {
                      props.onTrimValueChange(e.target.value), props.onErrorMessageChange("");
                    }}
                    onWheel={(e) => e.currentTarget.blur()}
                  />
                )}
                <div className="text-danger small text-center">
                  {props.trim === null ||
                    (props.trim === "" && (
                      <span data-test="quick-order-list-item-form-impeller-angle-error-message">
                        {"Enter a valid trim."}
                      </span>
                    ))}
                </div>
              </div>
            )}
          </div>
        </div>

        {/* Delete Button */}
        <div className="col col-lg-1 text-center">
          <button
            data-test="quick-order-form-delete-item-btn"
            type="button"
            className="btn btn-danger btn-md fs-6 mt-4 mb-4"
            onClick={() => {
              props.lineItemNumber !== null && props.onDeletePart(props.lineItemNumber);
            }}
          >
            <i className="fa fa-times fs-6"></i>
          </button>
        </div>
      </form>
    </Fragment>
  );
}

interface Props {
  loading: boolean;
  partFound: boolean;
  partNumberInvalid: boolean;
  errorMessage: string;
  inputValue: string;
  lineItemNumber: number | null;
  partId: number;
  partNumber: string;
  description: string;
  quantity: string;
  unitName: string;
  price: number;
  companyDiscount: number | null;
  weight: number | null;
  isPaintable: boolean;
  isImpeller: boolean;
  isFullTrim: boolean;
  trimOption: string | null;
  trim: string | null;
  angle: string | null;
  isSellable: boolean;
  paintColors: PaintColor[];
  selectedPaintColorId: number | null;
  searchComplete: boolean;
  onLoadingChange: (loading: boolean) => void;
  onErrorMessageChange: (errorMessage: string) => void;
  onInputValueChange: (inputValue: string) => void;
  onPartNumberChange: (partNumber: string) => void;
  onQuantityChange: (quantity: string) => void;
  onPaintColorChange: (paintColorId: number | null) => void;
  onTrimValueChange: (trimValue: string) => void;
  onTrimOptionChange: (trim: string) => void;
  onAngleChange: (angle: string) => void;
  onDeletePart: (lineItemNumber: number) => void;
  onPartNumberSearch: (partNumber: string) => void;
  onSearchComplete: (complete: boolean) => void;
  onPerformSearch: () => void;
}

interface PaintColor {
  paintColorId: number;
  hexCode: string;
  name: string;
}
